import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, GraduationCap } from 'lucide-react';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../Config/Config';

const RegisterModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: ''
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [coursesList, setCoursesList] = useState([]);

  React.useEffect(() => {
    const fetchCourses = async () => {
      try {
        const q = query(collection(db, 'courses'), where('status', '==', true));
        const snapshot = await getDocs(q);
        setCoursesList(snapshot.docs.map(doc => doc.data()));
      } catch (err) {
        console.error("Error fetching courses: ", err);
      }
    };
    fetchCourses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      await addDoc(collection(db, 'leads'), {
        ...formData,
        courseOfInterest: formData.course,
        type: 'Register Request',
        createdAt: new Date(),
        status: 'New'
      });
      setStatus('success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setFormData({ name: '', email: '', phone: '', course: '' });
      }, 3000);
    } catch (error) {
      console.error("Error submitting form: ", error);
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-cream w-full max-w-lg border border-white/20 shadow-2xl overflow-hidden relative"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-slate-400 hover:text-[#4A0F16] transition-colors"
            >
              <X size={24} />
            </button>

            <div className="bg-[#4A0F16] p-8 text-center border-b border-white/10">
              <div className="w-16 h-16 bg-white/10 text-white flex items-center justify-center mx-auto mb-4 border border-white/20">
                <GraduationCap size={32} />
              </div>
              <h3 className="font-serif text-3xl font-bold text-white mb-2">Register Now</h3>
              <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold">Take the first step towards your career</p>
            </div>

            <div className="p-8">
              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 bg-[#4A0F16]/10 text-[#4A0F16] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={32} />
                  </div>
                  <h4 className="font-serif text-2xl font-bold text-rich-black mb-2">Registration Successful</h4>
                  <p className="text-slate-600 font-light">Thank you for registering. Our academic counselor will contact you shortly.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 text-left">Full Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rich-black/10 focus:outline-none focus:border-[#4A0F16] transition-colors text-rich-black"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 text-left">Email Address</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rich-black/10 focus:outline-none focus:border-[#4A0F16] transition-colors text-rich-black"
                      placeholder="Your Email"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 text-left">Phone Number</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rich-black/10 focus:outline-none focus:border-[#4A0F16] transition-colors text-rich-black"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 text-left">Select Course</label>
                    <select
                      required
                      value={formData.course}
                      onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-rich-black/10 focus:outline-none focus:border-[#4A0F16] transition-colors text-rich-black appearance-none"
                    >
                      <option value="" disabled>Select a course program...</option>
                      {coursesList.map((course, idx) => (
                        <option key={idx} value={course.title}>{course.title}</option>
                      ))}
                    </select>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-xs text-center font-bold">An error occurred. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full mt-4 bg-[#4A0F16] text-white py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-[#8C363D] transition-colors disabled:opacity-50 border border-rich-black/10 shadow-sm"
                  >
                    {status === 'submitting' ? 'Submitting...' : 'Submit Registration'}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RegisterModal;
